/**
 * Campos Adicionales — constructor del formulario extra de solicitudes y ventas.
 *
 * El administrador define secciones ("Referencias del padre", "Referencias de
 * la madre", …) y dentro de cada una los campos que la empresa quiere capturar
 * (texto, número, monto, teléfono, fecha, opciones o sí/no, con marca de
 * requerido). La definición completa se guarda como un solo JSON por tenant
 * (/api/formularioextra_guardar) y las pantallas de solicitud de crédito y
 * ventas la leen para pintar la sección "Información adicional".
 *
 * Los valores capturados se guardan aparte, indexados por el id del campo
 * (nunca por el nombre): renombrar un campo no pierde los datos ya guardados.
 * Eliminar un campo o sección deja de mostrarlos, pero los valores históricos
 * permanecen en la tabla de datos.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader } from "components/financepro";
import { useApi } from "lib/api";

// Tipos de campo disponibles (mismo catálogo que renderizará la captura).
const TIPOS_CAMPO = [
  { value: "texto", label: "Texto" },
  { value: "numero", label: "Número" },
  { value: "monto", label: "Monto RD$" },
  { value: "telefono", label: "Teléfono" },
  { value: "fecha", label: "Fecha" },
  { value: "opciones", label: "Opciones" },
  { value: "sino", label: "Sí / No" },
];

const etiquetaTipo = (v) => TIPOS_CAMPO.find((t) => t.value === v)?.label || v;

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Ids únicos para secciones y campos nuevos. Los valores capturados se indexan
// por este id, así que jamás se reutiliza uno existente (Date.now + secuencia
// evita colisiones al crear varios en el mismo milisegundo).
let secuenciaId = 0;
const nuevoId = () => Date.now() * 1000 + (secuenciaId++ % 1000);

// tabVenta: pestaña de la VENTA A CRÉDITO donde sale la sección ("generales"
// o "prestamo"). Solo aplica ahí; en la solicitud y la venta al contado las
// secciones salen todas seguidas.
const TABS_VENTA = [
  { value: "generales", label: "Datos Generales" },
  { value: "prestamo", label: "Datos Préstamo" },
];

// codeudor: la sección describe a la persona (laboral, referencias, …) y se
// captura también para el co-deudor, con sus propios valores. Las de la
// persona alimentan la ficha por cédula que precarga a quien vuelva.
const seccionVacia = () => ({ id: nuevoId(), nombre: "", tabVenta: "prestamo", codeudor: false, campos: [] });
const campoVacio = () => ({
  id: nuevoId(),
  nombre: "",
  tipo: "texto",
  requerido: false,
  opciones: [],
});

// Intercambia posiciones i e i+dir dentro de una copia del arreglo.
const mover = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copia = [...arr];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
};

export default function CamposExtraConfig({ enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [secciones, setSecciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [sucio, setSucio] = useState(false);

  // Entrada temporal de opciones por campo (id de campo -> texto digitado);
  // vive fuera de la definición para no ensuciar el JSON que se guarda.
  const [opcionInput, setOpcionInput] = useState({});

  const [eliminandoSeccion, setEliminandoSeccion] = useState(null); // sección o null
  const [previa, setPrevia] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/formularioextra_obtener", {});
      if (ok) {
        // Secciones guardadas antes del selector de pestaña: van a "prestamo",
        // que es donde siempre habían salido en la venta a crédito.
        setSecciones(
          (data.formulario?.secciones || []).map((s) => ({ tabVenta: "prestamo", ...s }))
        );
        setSucio(false);
      } else {
        setSecciones([]);
        showAlert({
          message: data.Error || "No se pudo cargar la configuración del formulario",
          color: "warning",
        });
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toda mutación pasa por aquí para encender el indicador de cambios.
  const actualizar = (fn) => {
    setSecciones(fn);
    setSucio(true);
  };

  // ---- Secciones ----
  const agregarSeccion = () => actualizar((prev) => [...prev, seccionVacia()]);

  const setNombreSeccion = (id, nombre) =>
    actualizar((prev) => prev.map((s) => (s.id === id ? { ...s, nombre } : s)));

  const moverSeccion = (i, dir) => actualizar((prev) => mover(prev, i, dir));

  const pedirEliminarSeccion = (seccion) => {
    // Sin campos no hay nada que perder: se quita directo.
    if (!seccion.campos.length) {
      actualizar((prev) => prev.filter((s) => s.id !== seccion.id));
      return;
    }
    setEliminandoSeccion(seccion);
  };

  const eliminarSeccion = () => {
    actualizar((prev) => prev.filter((s) => s.id !== eliminandoSeccion.id));
    setEliminandoSeccion(null);
  };

  // ---- Campos ----
  const agregarCampo = (idSeccion) =>
    actualizar((prev) =>
      prev.map((s) => (s.id === idSeccion ? { ...s, campos: [...s.campos, campoVacio()] } : s))
    );

  const setCampo = (idSeccion, idCampo, cambios) =>
    actualizar((prev) =>
      prev.map((s) =>
        s.id === idSeccion
          ? {
              ...s,
              campos: s.campos.map((c) => (c.id === idCampo ? { ...c, ...cambios } : c)),
            }
          : s
      )
    );

  const moverCampo = (idSeccion, i, dir) =>
    actualizar((prev) =>
      prev.map((s) => (s.id === idSeccion ? { ...s, campos: mover(s.campos, i, dir) } : s))
    );

  const eliminarCampo = (idSeccion, idCampo) =>
    actualizar((prev) =>
      prev.map((s) =>
        s.id === idSeccion ? { ...s, campos: s.campos.filter((c) => c.id !== idCampo) } : s
      )
    );

  // ---- Opciones (tipo "opciones") ----
  const agregarOpcion = (idSeccion, campo) => {
    const texto = String(opcionInput[campo.id] || "").trim();
    if (!texto) return;
    if (campo.opciones.some((o) => o.toLowerCase() === texto.toLowerCase())) {
      setOpcionInput((prev) => ({ ...prev, [campo.id]: "" }));
      return;
    }
    setCampo(idSeccion, campo.id, { opciones: [...campo.opciones, texto] });
    setOpcionInput((prev) => ({ ...prev, [campo.id]: "" }));
  };

  const quitarOpcion = (idSeccion, campo, idx) =>
    setCampo(idSeccion, campo.id, {
      opciones: campo.opciones.filter((_, i) => i !== idx),
    });

  // ---- Guardar ----
  const validar = () => {
    for (let i = 0; i < secciones.length; i += 1) {
      const s = secciones[i];
      if (!String(s.nombre || "").trim()) return `La sección ${i + 1} no tiene nombre`;
      for (const c of s.campos) {
        if (!String(c.nombre || "").trim())
          return `Hay un campo sin nombre en la sección "${s.nombre}"`;
        if (c.tipo === "opciones" && !c.opciones.length)
          return `El campo "${c.nombre}" (${s.nombre}) es de opciones pero no tiene ninguna`;
      }
    }
    return null;
  };

  const guardar = async () => {
    const error = validar();
    if (error) {
      showAlert({ message: error, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const limpio = secciones.map((s) => ({
        id: s.id,
        nombre: String(s.nombre).trim(),
        tabVenta: s.tabVenta === "generales" ? "generales" : "prestamo",
        codeudor: !!s.codeudor,
        campos: s.campos.map((c) => ({
          id: c.id,
          nombre: String(c.nombre).trim(),
          tipo: c.tipo,
          requerido: !!c.requerido,
          opciones: c.tipo === "opciones" ? c.opciones : [],
        })),
      }));
      const { ok, data } = await post("/api/formularioextra_guardar", {
        formulario: { secciones: limpio },
      });
      if (ok) {
        showAlert({ message: "Formulario guardado", color: "success" });
        setSucio(false);
      } else {
        showAlert({ message: data.Error || "No se pudo guardar el formulario", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const totalCampos = secciones.reduce((n, s) => n + s.campos.length, 0);

  // ---- Render de un campo del constructor ----
  const filaCampo = (seccion, campo, i) => (
    <Box
      key={campo.id}
      sx={{
        border: `1px solid ${FP.color.border}`,
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 1.2,
        bgcolor: "#fff",
      }}
    >
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} sm={4.5}>
          <TextField
            fullWidth
            size="small"
            label="Nombre del campo"
            placeholder="Ej. Nombre del padre"
            value={campo.nombre}
            onChange={(e) => setCampo(seccion.id, campo.id, { nombre: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <TextField
            select
            fullWidth
            size="small"
            label="Tipo"
            value={campo.tipo}
            onChange={(e) => setCampo(seccion.id, campo.id, { tipo: e.target.value })}
          >
            {TIPOS_CAMPO.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2.5}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!campo.requerido}
                onChange={(e) => setCampo(seccion.id, campo.id, { requerido: e.target.checked })}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: FP.color.textSecondary }}>
                Requerido
              </Typography>
            }
          />
        </Grid>
        <Grid item xs={6} sm={2.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title="Subir">
            <span>
              <IconButton size="small" disabled={i === 0} onClick={() => moverCampo(seccion.id, i, -1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Bajar">
            <span>
              <IconButton
                size="small"
                disabled={i === seccion.campos.length - 1}
                onClick={() => moverCampo(seccion.id, i, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Eliminar campo">
            <IconButton
              size="small"
              onClick={() => eliminarCampo(seccion.id, campo.id)}
              sx={{ color: "#B3261E" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      {campo.tipo === "opciones" && (
        <Box sx={{ mt: 1.2 }}>
          {campo.opciones.length > 0 && (
            <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", gap: 0.8, mb: 1 }}>
              {campo.opciones.map((opt, idx) => (
                <Chip
                  key={`${campo.id}-${opt}`}
                  size="small"
                  label={opt}
                  onDelete={() => quitarOpcion(seccion.id, campo, idx)}
                  sx={{ fontWeight: 600, bgcolor: `${FP.color.navy}14`, color: FP.color.navy }}
                />
              ))}
            </Stack>
          )}
          <TextField
            fullWidth
            size="small"
            placeholder="Escriba una opción y presione Enter o coma"
            value={opcionInput[campo.id] || ""}
            onChange={(e) => setOpcionInput((prev) => ({ ...prev, [campo.id]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                agregarOpcion(seccion.id, campo);
              }
            }}
            onBlur={() => agregarOpcion(seccion.id, campo)}
          />
        </Box>
      )}
    </Box>
  );

  // ---- Render de la vista previa (como se verá en solicitud/venta) ----
  const previaCampo = (campo) => {
    const etiqueta = campo.nombre + (campo.requerido ? " *" : "");
    if (campo.tipo === "opciones")
      return (
        <TextField select fullWidth size="small" label={etiqueta} value="" disabled>
          {campo.opciones.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      );
    if (campo.tipo === "sino")
      return (
        <FormControlLabel
          control={<Switch size="small" disabled />}
          label={<Typography sx={{ fontSize: "0.85rem" }}>{etiqueta}</Typography>}
        />
      );
    return (
      <TextField
        fullWidth
        size="small"
        disabled
        label={etiqueta}
        type={campo.tipo === "fecha" ? "date" : "text"}
        InputLabelProps={campo.tipo === "fecha" ? { shrink: true } : undefined}
        InputProps={
          campo.tipo === "monto"
            ? { startAdornment: <InputAdornment position="start">RD$</InputAdornment> }
            : undefined
        }
      />
    );
  };

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Campos Adicionales"
        subtitle="Secciones y campos extra que se capturan en solicitudes de crédito y ventas"
        cta={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<VisibilityOutlinedIcon />}
              onClick={() => setPrevia(true)}
              disabled={!totalCampos}
              sx={btnGhost}
            >
              Vista previa
            </Button>
            <Button startIcon={<AddIcon />} onClick={agregarSeccion} sx={btnOutline(FP.color.navy)}>
              Nueva sección
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={guardando || !sucio}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {cargando ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress size={30} />
          </Stack>
        ) : secciones.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 8, color: FP.color.textMuted }}>
            <InboxOutlinedIcon sx={{ fontSize: 48, mb: 1.2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
              Aún no hay secciones configuradas
            </Typography>
            <Typography sx={{ fontSize: "0.87rem", mb: 2.5 }}>
              Cree la primera sección — por ejemplo &quot;Referencias del padre&quot; — y agréguele campos.
            </Typography>
            <Button startIcon={<AddIcon />} onClick={agregarSeccion} sx={btnOutline(FP.color.navy)}>
              Nueva sección
            </Button>
          </Stack>
        ) : (
          <>
            {sucio && (
              <Typography
                sx={{ mb: 2, fontSize: "0.83rem", fontWeight: 700, color: "#B3261E" }}
              >
                Hay cambios sin guardar
              </Typography>
            )}
            {secciones.map((seccion, i) => (
              <Box
                key={seccion.id}
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2.5,
                  p: { xs: 2, sm: 2.6 },
                  mb: 2.5,
                  bgcolor: FP.color.cardBg,
                  boxShadow: FP.shadow.soft,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.2,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${FP.color.navy}14`,
                      color: FP.color.navy,
                      flexShrink: 0,
                    }}
                  >
                    <DynamicFormOutlinedIcon sx={{ fontSize: 19 }} />
                  </Box>
                  <TextField
                    size="small"
                    label="Nombre de la sección"
                    placeholder="Ej. Referencias del padre"
                    value={seccion.nombre}
                    onChange={(e) => setNombreSeccion(seccion.id, e.target.value)}
                    sx={{ flex: 1, maxWidth: 420 }}
                  />
                  {/* Sin Tooltip: su popup tapaba las opciones del desplegable. */}
                  <TextField
                    select
                    size="small"
                    label="Pestaña en venta a crédito"
                    value={seccion.tabVenta === "generales" ? "generales" : "prestamo"}
                    onChange={(e) =>
                      actualizar((prev) =>
                        prev.map((s) => (s.id === seccion.id ? { ...s, tabVenta: e.target.value } : s))
                      )
                    }
                    sx={{ width: 210 }}
                  >
                    {TABS_VENTA.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!seccion.codeudor}
                        onChange={(e) =>
                          actualizar((prev) =>
                            prev.map((s) => (s.id === seccion.id ? { ...s, codeudor: e.target.checked } : s))
                          )
                        }
                      />
                    }
                    label="También al fiador / co-deudor"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.82rem", fontWeight: 600 } }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: FP.color.textMuted,
                      ml: "auto",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {seccion.campos.length} campo{seccion.campos.length === 1 ? "" : "s"}
                  </Typography>
                  <Tooltip title="Subir sección">
                    <span>
                      <IconButton size="small" disabled={i === 0} onClick={() => moverSeccion(i, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Bajar sección">
                    <span>
                      <IconButton
                        size="small"
                        disabled={i === secciones.length - 1}
                        onClick={() => moverSeccion(i, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Eliminar sección">
                    <IconButton
                      size="small"
                      onClick={() => pedirEliminarSeccion(seccion)}
                      sx={{ color: "#B3261E" }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {seccion.campos.map((campo, j) => filaCampo(seccion, campo, j))}

                <Button
                  size="small"
                  startIcon={<PlaylistAddOutlinedIcon />}
                  onClick={() => agregarCampo(seccion.id)}
                  sx={{ ...btnGhost, mt: seccion.campos.length ? 0.5 : 0 }}
                >
                  Agregar campo
                </Button>
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* Confirmar eliminación de sección con campos */}
      <Dialog
        open={eliminandoSeccion !== null}
        onClose={() => setEliminandoSeccion(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar sección</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La sección <b>{eliminandoSeccion?.nombre || "(sin nombre)"}</b> tiene{" "}
            <b>{eliminandoSeccion?.campos.length}</b> campo
            {eliminandoSeccion?.campos.length === 1 ? "" : "s"}. Al eliminarla dejarán de
            capturarse en solicitudes y ventas; los valores ya guardados no se pierden.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminandoSeccion(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={<DeleteOutlineIcon />}
            onClick={eliminarSeccion}
            sx={btnSolid("#B3261E", "#8C1D18")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vista previa: cómo se verá la sección "Información adicional" */}
      <Dialog
        open={previa}
        onClose={() => setPrevia(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Vista previa — Información adicional</DialogTitle>
        <DialogContent>
          {secciones
            .filter((s) => s.campos.length)
            .map((seccion) => (
              <Box key={seccion.id} sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.74rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: FP.color.textSecondary,
                    }}
                  >
                    {seccion.nombre || "(sin nombre)"}
                  </Typography>
                  <Chip
                    size="small"
                    label={`Venta a crédito: ${
                      seccion.tabVenta === "generales" ? "Datos Generales" : "Datos Préstamo"
                    }`}
                    sx={{ fontWeight: 600, fontSize: "0.68rem", bgcolor: `${FP.color.navy}14`, color: FP.color.navy }}
                  />
                  {seccion.codeudor && (
                    <Chip
                      size="small"
                      label="También al fiador / co-deudor"
                      sx={{ fontWeight: 600, fontSize: "0.68rem", bgcolor: `${FP.color.navy}14`, color: FP.color.navy }}
                    />
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  {seccion.campos.map((campo) => (
                    <Grid item xs={12} sm={6} key={campo.id}>
                      {previaCampo(campo)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted, mt: 1 }}>
            Los tipos {TIPOS_CAMPO.map((t) => t.label).join(", ")} se capturan igual en la
            solicitud de crédito y en las ventas directas.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPrevia(false)} sx={btnGhost}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
